import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Profile, Account } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'

// Schema for editing existing user
const editUserSchema = z.object({
  full_name: z.string().optional(),
  role: z.enum(['admin', 'user']),
  account_id: z.string().nullable(),
  status: z.enum(['active', 'suspended', 'deleted']),
})

// Schema for creating invitation
const inviteUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  role: z.enum(['admin', 'user']),
  account_id: z.string().nullable(),
})

type EditUserFormData = z.infer<typeof editUserSchema>
type InviteUserFormData = z.infer<typeof inviteUserSchema>

export function UserFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user: currentUser } = useAuth()
  const isEditing = !!id

  // Fetch accounts for dropdown
  const { data: accounts } = useQuery({
    queryKey: ['accounts-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .eq('status', 'active')
        .order('name')
      if (error) throw error
      return data as Pick<Account, 'id' | 'name'>[]
    },
  })

  // Form for editing
  const editForm = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      full_name: '',
      role: 'user',
      account_id: null,
      status: 'active',
    },
  })

  // Form for inviting
  const inviteForm = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      email: '',
      role: 'user',
      account_id: null,
    },
  })

  // Fetch existing user if editing
  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as Profile
    },
    enabled: isEditing,
  })

  // Populate edit form with existing data
  useEffect(() => {
    if (user) {
      editForm.reset({
        full_name: user.full_name || '',
        role: user.role as 'admin' | 'user',
        account_id: user.account_id,
        status: user.status as 'active' | 'suspended' | 'deleted',
      })
    }
  }, [user, editForm])

  // Edit mutation
  const editMutation = useMutation({
    mutationFn: async (data: EditUserFormData) => {
      if (!id) throw new Error('User ID is required')
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name || null,
          role: data.role,
          account_id: data.account_id,
          status: data.status,
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast({
        title: 'User updated',
        description: 'The user has been successfully updated.',
      })
      navigate('/admin/users')
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: InviteUserFormData) => {
      // Generate invitation token
      const { data: tokenData, error: tokenError } = await supabase.rpc(
        'generate_invitation_token'
      )
      if (tokenError) throw tokenError

      // Create invitation
      const { data: invitation, error } = await supabase
        .from('invitations')
        .insert({
          email: data.email.trim().toLowerCase(),
          role: data.role,
          account_id: data.account_id,
          invited_by: currentUser?.id,
          token: tokenData,
          expires_at: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000
          ).toISOString(),
        })
        .select('id')
        .single()
      if (error) throw error

      // Send the invitation email (recipient/token are read from the DB row)
      const { error: emailError } = await supabase.functions.invoke(
        'send-notification-email',
        { body: { type: 'invite', invitation_id: invitation.id } }
      )

      return { token: tokenData, emailSent: !emailError }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      if (data.emailSent) {
        toast({
          title: 'Invitation sent',
          description: 'An invitation email has been sent.',
        })
      } else {
        toast({
          title: 'Invitation created',
          description: `Email could not be sent — share this link instead: ${window.location.origin}/accept-invite/${data.token}`,
          variant: 'destructive',
        })
      }
      navigate('/admin/users')
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onEditSubmit = (data: EditUserFormData) => {
    editMutation.mutate(data)
  }

  const onInviteSubmit = (data: InviteUserFormData) => {
    inviteMutation.mutate(data)
  }

  if (isEditing && isLoadingUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/users">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? 'Edit User' : 'Invite User'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing
              ? 'Update user information and permissions'
              : 'Send an invitation to a new user'}
          </p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>{isEditing ? 'User Details' : 'Invitation Details'}</CardTitle>
          <CardDescription>
            {isEditing
              ? 'Manage user profile and access'
              : 'Enter the details for the new user invitation'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Form {...editForm}>
              <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <FormLabel>Email</FormLabel>
                  <Input value={user?.email || ''} disabled />
                  <p className="text-sm text-muted-foreground">
                    Email cannot be changed
                  </p>
                </div>

                <FormField
                  control={editForm.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} value={field.value || ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Admins have full platform access. Users are scoped to their account.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                        value={field.value || 'none'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select account" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No account</SelectItem>
                          {accounts?.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Assign user to a specific limo company account
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                          <SelectItem value="deleted">Deleted</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Suspended and deleted users cannot access the platform
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-4">
                  <Button type="submit" disabled={editMutation.isPending}>
                    {editMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Update User
                  </Button>
                  <Button type="button" variant="outline" asChild>
                    <Link to="/admin/users">Cancel</Link>
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <Form {...inviteForm}>
              <form onSubmit={inviteForm.handleSubmit(onInviteSubmit)} className="space-y-6">
                <FormField
                  control={inviteForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="user@example.com" {...field} />
                      </FormControl>
                      <FormDescription>
                        The invitation link will be generated for this email
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={inviteForm.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="user">User</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Admins have full platform access. Users are scoped to their account.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={inviteForm.control}
                  name="account_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account</FormLabel>
                      <Select
                        onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                        value={field.value || 'none'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select account" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No account (for admins)</SelectItem>
                          {accounts?.map((account) => (
                            <SelectItem key={account.id} value={account.id}>
                              {account.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Required for user role. Leave empty for admin invitations.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-4">
                  <Button type="submit" disabled={inviteMutation.isPending}>
                    {inviteMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Create Invitation
                  </Button>
                  <Button type="button" variant="outline" asChild>
                    <Link to="/admin/users">Cancel</Link>
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
